const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/setup/configureStore.ts';
import Imm from 'immutable';
import { Action, applyMiddleware, createStore } from 'redux';
import { thunk } from 'redux-thunk';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import { composeWithDevTools } from 'redux-devtools-extension';
import sagas from '../../sagas';
import { patapi } from 'App/middleware';
import { PrefLang } from 'App/defs/loginDefs';
import { isMatomoEnabled, matomoHandler } from 'App/utils/matomo';
import { rootReducer } from './rootReducer';
import { createReduxHistory, routerMiddleware } from './routerHistory';
import { atSetI18nLocaleSuccess, SetI18nLocaleSuccessAction } from '@cistec/common_web/actions/intlActions';

declare const module: any;
declare const require: any;
declare const window: any;

let logger: any = null;

if (process.env.NODE_ENV !== 'production') {
    logger = createLogger({
        collapsed: true,
        // The store holds mostly Immutable.js slices, but some are plain JS (e.g. the
        // redux-first-history `router` slice). Follow redux-logger's documented Immutable.js
        // recipe: convert Immutable structures with toJS() and pass plain values through.
        stateTransformer: (state: any) =>
            Object.keys(state).reduce(
                (prev: any, curr: any) =>
                    Object.assign(prev, {
                        [curr]: Imm.Iterable.isIterable(state[curr]) ? state[curr].toJS() : state[curr],
                    }),
                {}
            ),
        predicate: (_getState: any, action: any) =>
            !['EFFECT_TRIGGERED', 'EFFECT_RESOLVED', 'EFFECT_REJECTED'].includes(action.type),
    });
}

const onSwitchLangSaveToSessionStorage = () => {
    return (next: any) => (action: Action) => {
        if (action.type === atSetI18nLocaleSuccess) {
            const theAction = action as SetI18nLocaleSuccessAction;
            console.log(`Updating localStorage: locale is now ${theAction.meta.locale}`);
            localStorage.setItem(PrefLang, JSON.stringify(theAction.meta.locale));
        }
        return next(action);
    };
};

// saga handling middleware factory
const sagaMiddleware = createSagaMiddleware();

// router

const reduxRouterMiddleware = routerMiddleware;

export let routerHistory: ReturnType<typeof createReduxHistory>;

// define global redux store for production
function productionStore() {
    if (isMatomoEnabled) {
        return applyMiddleware(reduxRouterMiddleware, patapi, thunk, sagaMiddleware, matomoHandler as any);
    } else {
        return applyMiddleware(reduxRouterMiddleware, patapi, thunk, sagaMiddleware);
    }
}

const composeEnhancers = composeWithDevTools({ maxAge: 200 });

// define global redux store for development including redux-devtools
// Beware that the order of the middlewares and store enhancers may have an impact
// on the correct functioning
function developmentStore() {
    if (isMatomoEnabled) {
        return composeEnhancers(
            applyMiddleware(
                reduxRouterMiddleware,
                patapi,
                thunk,
                sagaMiddleware,
                logger,
                matomoHandler as any,
                onSwitchLangSaveToSessionStorage
            )
        );
    } else {
        return composeEnhancers(
            applyMiddleware(
                reduxRouterMiddleware,
                patapi,
                thunk,
                sagaMiddleware,
                logger,
                onSwitchLangSaveToSessionStorage
            )
        );
    }
}

export default function configureStore(initialState: any = {}) {
    const store =
        process.env.NODE_ENV !== 'production'
            ? createStore(rootReducer(), initialState, developmentStore())
            : createStore(rootReducer(), initialState, productionStore());

    routerHistory = createReduxHistory(store);

    sagaMiddleware.run(sagas);

    if (module.hot) {
        // enable webpack hot module replacement for reducers
        module.hot.accept('./rootReducer', () => {
            const nextRootReducer = require('./rootReducer');
            store.replaceReducer(nextRootReducer);
        });
    }

    if (process.env.NODE_ENV !== 'production') {
        window.__redux__ = store;
    }

    return store;
}
